/**
 * Conocimiento — la biblioteca REAL (biblioteca.html, ADR-14 pts 5–7): los
 * cuadernos de `content/library` tal como los sirve la API, con su recuento
 * por estado de git, las preguntas que faltan, las firmas, el dueño y la
 * última firma. Elegir un área = hacerse dueño de sus cuadernos LIBRES (un
 * solo dueño por cuaderno; las áreas sin libres van cerradas con porqué).
 * «Abrir» lleva a la primera ficha del cuaderno con una pregunta pendiente.
 * Estados: lista / vacío (un socio sin cuadernos) / cargando / error (se lee
 * la copia, no se escribe) y sin rol de biblioteca. `?estado=` los fuerza
 * para revisión; `?q=Cnn` abre la tabla buscando ese cuaderno.
 *
 * Recibe el cliente por props (`api`): la página lo ata al real y los tests
 * a uno falso, sin MSAL.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "@skein/api-client";
import type { Biblioteca as BibliotecaData, CuadernoResumen } from "@skein/contracts";
import {
  Avatar, Badge, Banner, Button, CellTitle, Check, Count, Dialog, Empty, Fainted, Num, PageBtn, PageHead, Pagination, Pill, PillGroup, Progress,
  RowActions, SearchInput, Select, SidepaneStack, Skeleton, Sub, Table, TdNum, Th, Toolbar, Vh, useSlashFocus, useToast, type SortDir,
} from "@skein/ui";
import { initials } from "../../auth/initials";
import { useAsideSlot } from "../../shell/asideSlot";
import { useBibliotecaDe, useDueno, type BibliotecaApi, type TelemetriaApi } from "../../api/biblioteca";
import { areas, esperanPersona, etiquetaArea, filtrar, haceCuanto, porEstado, type Area, type FiltroEstado, type Orden } from "./logica";
import { MiRevision, useAbrirCuaderno } from "./MiRevision";
import { UsoDespacho } from "./UsoDespacho";

const POR_PAG = 15;
const SIN_CONEXION = "Sin conexión: ahora no se puede cambiar nada.";
const DESPACHO = "frontera profesional · pasa por el despacho";
const ESTADOS = [
  { value: "", label: "Todos los estados" },
  { value: "preguntas", label: "Con preguntas" },
  { value: "mias", label: "Firmadas por mí" },
  { value: "persona", label: "Esperan a una persona" },
] as const;

function SkeletonRows() {
  const s = (w: string, right?: boolean) => <Skeleton style={{ width: w, ...(right ? { marginLeft: "auto" } : {}) }} />;
  return (
    <tbody>
      {Array.from({ length: 8 }, (_, i) => (
        <tr key={i}>
          <td>{s("9rem")}</td><td>{s("1.5rem")}</td><TdNum>{s("1.5rem", true)}</TdNum><TdNum>{s("2rem", true)}</TdNum><TdNum>{s("4rem", true)}</TdNum>
          <td>{s("5rem")}</td><td>{s("4rem")}</td><td />
        </tr>
      ))}
    </tbody>
  );
}

function Cabeceras({ orden, sort, vivo }: { orden: Orden; sort: (k: Orden["k"]) => void; vivo: boolean }) {
  const sorted = (k: Orden["k"]): SortDir | null => (orden.k === k ? (orden.dir < 0 ? "descending" : "ascending") : null);
  return (
    <thead><tr>
      <Th sortable={vivo} sorted={sorted("code")} onSort={() => sort("code")}>Cuaderno</Th>
      <Th>Área</Th>
      <Th num>Fichas</Th>
      <Th num sortable={vivo} sorted={sorted("pend")} onSort={() => sort("pend")} tip="Preguntas pendientes de contestar">Preguntas</Th>
      <Th num sortable={vivo} sorted={sorted("firm")} onSort={() => sort("firm")} tip="Fichas con el criterio firmado">Firmas</Th>
      <Th>Dueño</Th>
      <Th>Última firma</Th>
      <Th aria-label="Acciones" />
    </tr></thead>
  );
}

/** Una fila de cuaderno. `vivo=false`: la copia congelada del error (sin foco, sin actos). */
function Fila({ c, me, vivo, onAbrir, onDueno }: { c: CuadernoResumen; me: BibliotecaData["me"]; vivo: boolean; onAbrir: () => void; onDueno: () => void }) {
  const p = c.preguntasPendientes;
  const pct = c.fichas ? Math.round((c.firmadas / c.fichas) * 100) : 0;
  const ultima = haceCuanto(c.ultimaFirma);
  return (
    <tr tabIndex={vivo ? 0 : undefined} onClick={vivo ? onAbrir : undefined} onKeyDown={vivo ? (e) => { if (e.key === "Enter" && e.target === e.currentTarget) { e.preventDefault(); onAbrir(); } } : undefined}>
      {/* el título real puede ser largo («…internacionalización…»): se parte con guion para que la columna no ensanche la tabla */}
      <CellTitle hyphens title={c.title} tag={<Fainted className="sk-mono">{c.code}</Fainted>} sub={c.professionalReview ? DESPACHO : undefined} />
      <td>{etiquetaArea(c)}</td>
      <TdNum>{c.fichas}<Vh> fichas · </Vh><Sub inline>{porEstado(c)}</Sub></TdNum>
      <TdNum>{p ? <Count hot>{p}</Count> : "—"}{c.preguntasTotal > 0 && <><Vh> preguntas pendientes · </Vh><Sub inline>de {c.preguntasTotal}</Sub></>}</TdNum>
      <TdNum><div className="signed"><Progress value={c.fichas ? c.firmadas / c.fichas : 0} label={`${pct} % firmadas`} /><Num>{c.firmadas ? `${c.firmadas}/${c.fichas}` : "—"}</Num></div></TdNum>
      <td>
        {c.dueno
          ? <span className="owner"><Avatar size="s">{initials(c.dueno.name)}</Avatar>{c.dueno.oid === me.oid ? "Tú" : c.dueno.name}</span>
          : <span className="owner"><Fainted>sin dueño</Fainted>{vivo && <Button variant="link" onClick={(e) => { e.stopPropagation(); onDueno(); }}>hacerme dueño</Button>}</span>}
      </td>
      <td>{ultima ?? <Fainted>—</Fainted>}</td>
      {vivo ? <RowActions><Button variant="quiet" size="s" onClick={(e) => { e.stopPropagation(); onAbrir(); }}>Abrir</Button></RowActions> : <td />}
    </tr>
  );
}

/** «Elegir áreas…» (ADR-14 pt 5): la regla es POR CUADERNO — un área toma sus cuadernos libres. */
function ElegirAreas({ api, data, open, marcar, onClose }: { api: BibliotecaApi; data: BibliotecaData; open: boolean; marcar: string | null; onClose: () => void }) {
  const say = useToast();
  const dueno = useDueno(api);
  const me = data.me;
  const lista = useMemo(() => areas(data.cuadernos), [data.cuadernos]);
  const mia = (a: Area) => a.cuadernos.some((c) => c.dueno?.oid === me.oid);
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const [guardando, setGuardando] = useState(false);
  useEffect(() => {
    if (open) setElegidas(new Set(lista.filter((a) => mia(a) || a.code === marcar).map((a) => a.code)));
    // al abrir, y solo entonces: lo marcado se respeta mientras la modal esté abierta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const nombre = me.name.split(/\s+/)[0] ?? me.name;

  const guardar = async () => {
    setGuardando(true);
    let nuevos = 0, sueltos = 0, perdidos = 0;
    try {
      for (const a of lista) {
        for (const c of a.cuadernos) {
          const tengo = c.dueno?.oid === me.oid;
          if (elegidas.has(a.code) && !c.dueno) {
            try { await dueno.mutateAsync({ code: c.code, body: { accion: "tomar" } }); nuevos++; } catch (e) { if (e instanceof ApiError && e.status === 409) perdidos++; else throw e; }
          } else if (!elegidas.has(a.code) && tengo) {
            await dueno.mutateAsync({ code: c.code, body: { accion: "soltar" } }); sueltos++;
          }
        }
      }
      onClose();
      say(nuevos ? `Eres dueño de ${nuevos} cuaderno${nuevos > 1 ? "s" : ""} más. Sus preguntas ya están en «Mi revisión».`
        : sueltos ? `Sueltas ${sueltos} cuaderno${sueltos > 1 ? "s" : ""}: quedan libres para otro socio.`
        : perdidos ? "Otro socio se adelantó: esos cuadernos ya tienen dueño." : "Tus áreas quedan como estaban.");
    } catch {
      // lo que ya se hizo, hecho está (cada cuaderno es un registro): se dice qué quedó a medias
      const hecho = [nuevos ? `tomaste ${nuevos}` : "", sueltos ? `soltaste ${sueltos}` : ""].filter(Boolean).join(" y ");
      say(hecho ? `No llego a la biblioteca a mitad del guardado: ${hecho} cuaderno${nuevos + sueltos > 1 ? "s" : ""}; el resto quedó como estaba. Vuelve a guardar.`
        : "No llego a la biblioteca: tus áreas quedan como estaban. Reintento solo cada 30 s.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => { if (!guardando) onClose(); }} title={`Tus áreas, ${nombre}`}
      subtitle="Elegir un área te hace dueño de sus cuadernos libres: contestas sus preguntas y firmas el criterio de la casa con tu nombre. Cada cuaderno tiene un solo dueño; los que ya lo tienen no cambian."
      initial={marcar ? `[id="area-${marcar}"]` : undefined}
      footer={<>
        <Button variant="ghost" aria-disabled={guardando || undefined} tip={guardando ? "Guardando tus áreas…" : undefined}
          onClick={() => { if (guardando) say("Guardando tus áreas…: un momento."); else onClose(); }}>Cancelar</Button>
        <Button variant="primary" loading={guardando} onClick={() => void guardar()}>Guardar mis áreas</Button>
      </>}>
      <div className="areas" role="group" aria-label="Áreas de la biblioteca">
        {lista.map((a) => {
          const mios = a.cuadernos.filter((c) => c.dueno?.oid === me.oid);
          const libres = a.cuadernos.filter((c) => !c.dueno);
          const cerrada = !mios.length && !libres.length;
          const quien = a.cuadernos.map((c) => `${c.code} ${c.dueno?.oid === me.oid ? "tuyo" : c.dueno ? c.dueno.name : "libre"}`).join(" · ");
          const porque = cerrada ? " — todos sus cuadernos tienen dueño" : libres.length && (mios.length || libres.length < a.cuadernos.length) ? ` — ${libres.length} libre${libres.length > 1 ? "s" : ""}` : "";
          return (
            <Check key={a.code} id={`area-${a.code}`} label={a.label} help={`${quien}${porque}`} checked={elegidas.has(a.code)} closed={cerrada}
              onClosed={() => say("Todos los cuadernos de esa área ya tienen dueño.")}
              onChange={(on) => setElegidas((s) => { const n = new Set(s); if (on) n.add(a.code); else n.delete(a.code); return n; })} />
          );
        })}
      </div>
    </Dialog>
  );
}

/** `telemetria`: el cliente de los contadores de uso (ADR-20 pt 4); sin él, «Uso en el Despacho» se queda cargando. `uso` fuerza su estado (`?uso=`). */
export function Biblioteca({ api, telemetria, estado, uso, q: q0 }: { api: BibliotecaApi; telemetria?: TelemetriaApi | undefined; estado?: string | undefined; uso?: string | undefined; q?: string | undefined }) {
  const b = useBibliotecaDe(api);
  const say = useToast();
  const abrir = useAbrirCuaderno(api);
  const inputRef = useSlashFocus();
  const [q, setQ] = useState(q0 ?? "");
  useEffect(() => { setQ(q0 ?? ""); setPag(1); }, [q0]);
  const [area, setArea] = useState("");
  const [est, setEst] = useState<FiltroEstado>("");
  const [orden, setOrden] = useState<Orden>({ k: "pend", dir: -1 });
  const [pag, setPag] = useState(1);
  const [soloMios, setSoloMios] = useState<boolean | null>(null);
  const [modal, setModal] = useState<{ marcar: string | null } | null>(null);

  const data = b.data;
  const sinRol = b.error instanceof ApiError && b.error.status === 403;
  const mios = data?.me.misCuadernos ?? [];
  // un socio sin cuadernos llega a «Mis cuadernos»: lo primero es elegir sus áreas
  const solo = soloMios ?? (estado === "vacio" || (data?.me.rol === "biblioteca" && mios.length === 0));
  const forced = estado === "vacio" || estado === "cargando" || estado === "error" ? estado : undefined;
  const view = forced
    ?? (b.isPending ? "cargando" : sinRol ? "sin-rol" : b.isError && !data ? "error-sin-datos" : b.isError ? "error" : solo && mios.length === 0 ? "vacio" : "lista");
  const sinConexion = view === "error" || view === "error-sin-datos";

  const titulos = useMemo(() => new Map((data?.cuadernos ?? []).map((c) => [c.code, c.title])), [data?.cuadernos]);
  // la columna: «Uso en el Despacho» (va por su cuenta: si cae, la biblioteca se sigue leyendo) encima de «Mi revisión»
  useAsideSlot(view === "sin-rol" ? null : (
    <SidepaneStack className="aside" aria-label="Uso y revisión">
      <UsoDespacho api={telemetria} rol={data?.me.rol} forzada={uso} cuadernos={titulos} />
      <MiRevision seccion api={api} biblioteca={view === "vacio" ? undefined : data} cargando={view === "cargando"} sinConexion={sinConexion} />
    </SidepaneStack>
  ));

  const filas = useMemo(() => (data ? filtrar(data.cuadernos, { q, area, estado: est, soloMios: solo, mios, orden }) : []), [data, q, area, est, solo, mios, orden]);
  const pags = Math.max(1, Math.ceil(filas.length / POR_PAG));
  const pagina = Math.min(pag, pags);
  const i0 = (pagina - 1) * POR_PAG;
  const vis = filas.slice(i0, i0 + POR_PAG);
  // la altura de la tabla se fija tras el primer pintado: en la última página (más corta) el paginador NO salta
  const wrap = useRef<HTMLDivElement>(null);
  const [minH, setMinH] = useState<number | undefined>(undefined);
  useLayoutEffect(() => { if (view === "lista" && minH === undefined && vis.length === POR_PAG && wrap.current) setMinH(wrap.current.offsetHeight); }, [view, minH, vis.length]);

  const sort = (k: Orden["k"]) => { setOrden((o) => (o.k === k ? { k, dir: o.dir === 1 ? -1 : 1 } : { k, dir: k === "code" ? 1 : -1 })); setPag(1); };
  const abrirAreas = (marcar: string | null = null) => { if (sinConexion) { say(SIN_CONEXION); return; } setModal({ marcar }); };
  const total = data?.cuadernos.reduce((s, c) => s + c.fichas, 0) ?? 0;
  const pendientes = data?.cuadernos.reduce((s, c) => s + c.preguntasPendientes, 0) ?? 0;
  const persona = data?.cuadernos.reduce((s, c) => s + esperanPersona(c), 0) ?? 0;
  const glosa = { fontSize: "var(--sk-fs-2xs)", alignSelf: "center" } as const;

  if (view === "sin-rol") {
    return (
      <>
        <PageHead title="Conocimiento" />
        <Empty glyph="✳" title="Tu cuenta no tiene el rol de biblioteca">
          Con él lees los cuadernos de la biblioteca y firmas el criterio de los tuyos. Pídeselo a un admin de Skein: se da en Entra ID y no abre nada más del Studio.
        </Empty>
      </>
    );
  }

  const meta = view === "cargando" || !data ? (view === "cargando" ? <Skeleton style={{ width: "22rem", height: "1.25em" }} /> : undefined)
    : view === "vacio" ? <><Badge>0 cuadernos tuyos</Badge><Fainted style={glosa}>la biblioteca tiene {data.cuadernos.length} cuadernos y {total} fichas</Fainted></>
    : <>
      <Badge>{data.cuadernos.length} cuadernos</Badge>
      <Badge>{total} fichas</Badge>
      <Count hot={pendientes > 0}>{pendientes}</Count><Fainted style={glosa}>de {data.preguntasTotal} preguntas pendientes</Fainted>
      {persona > 0 && <Badge tone="warn">{persona} {persona === 1 ? "espera" : "esperan"} a una persona</Badge>}
    </>;

  const tabla = (vivo: boolean, xs: readonly CuadernoResumen[]) => (
    <Table stale={!vivo} label="Cuadernos de la biblioteca" wrapRef={vivo ? wrap : undefined} style={vivo && minH !== undefined ? { minHeight: minH } : undefined}>
      <Cabeceras orden={orden} sort={sort} vivo={vivo} />
      <tbody>
        {data && xs.map((c) => <Fila key={c.code} c={c} me={data.me} vivo={vivo} onAbrir={() => void abrir(c.code)} onDueno={() => abrirAreas(c.area)} />)}
        {vivo && xs.length === 0 && (
          <tr><td colSpan={8}>
            <Empty inline title={est === "mias" ? "Aún no has firmado ninguna ficha con ese filtro." : "Nada casa con ese filtro."} style={{ margin: "var(--sk-sp-3)" }}>
              {est === "mias" ? "Tus firmas aparecerán aquí en cuanto contestes la primera pregunta." : "Prueba a quitar el área o el estado."}
            </Empty>
          </td></tr>
        )}
      </tbody>
    </Table>
  );

  return (
    <>
      <PageHead
        title="Conocimiento"
        meta={meta}
        actions={<>
          <PillGroup label="Qué cuadernos ver">
            <Pill pressed={!solo} stale={sinConexion} tip={sinConexion ? SIN_CONEXION : undefined} onClick={() => { setSoloMios(false); setPag(1); }}>Todos</Pill>
            <Pill pressed={solo} stale={sinConexion} tip={sinConexion ? SIN_CONEXION : undefined} onClick={() => { setSoloMios(true); setPag(1); }}>Mis cuadernos</Pill>
          </PillGroup>
          <Button variant="primary" stale={sinConexion} tip={sinConexion ? SIN_CONEXION : undefined} onClick={() => abrirAreas()}>Elegir áreas…</Button>
        </>}
      />
      <div className="listzone">
        {view === "lista" && (
          <Toolbar>
            <SearchInput inputRef={inputRef} style={{ flex: 1, minWidth: "14rem" }} placeholder="Buscar un cuaderno…" aria-label="Buscar un cuaderno" shortcut="/" value={q} onChange={(e) => { setQ(e.target.value); setPag(1); }} />
            <Select ariaLabel="Área" style={{ width: "16rem" }} value={area} onChange={(v) => { setArea(v); setPag(1); }}
              options={[{ value: "", label: "Todas las áreas" }, ...areas(data?.cuadernos ?? []).map((a) => ({ value: a.code, label: a.label }))]} />
            <Select ariaLabel="Estado" style={{ width: "14rem" }} value={est} onChange={(v) => { setEst(v); setPag(1); }} options={ESTADOS} />
          </Toolbar>
        )}

        {view === "lista" && (
          <section>
            {tabla(true, vis)}
            <div className="list-foot" style={{ marginTop: "var(--sk-sp-4)" }}>
              <Pagination meta={filas.length ? `${i0 + 1}–${Math.min(i0 + POR_PAG, filas.length)} de ${filas.length} cuadernos · ${POR_PAG} por página` : `0 de ${data?.cuadernos.length ?? 0}`}>
                <PageBtn aria-label="Página anterior" disabled={pagina <= 1} onClick={() => setPag(pagina - 1)}>‹</PageBtn>
                {Array.from({ length: pags }, (_, i) => <PageBtn key={i} current={i + 1 === pagina} onClick={() => setPag(i + 1)}>{i + 1}</PageBtn>)}
                <PageBtn aria-label="Página siguiente" disabled={pagina >= pags} onClick={() => setPag(pagina + 1)}>›</PageBtn>
              </Pagination>
            </div>
          </section>
        )}

        {view === "vacio" && (
          <Empty glyph="✳" title="Aún no tienes cuadernos: elige tus áreas">
            Elegir un área te hace dueño de sus cuadernos: el asesor te hará sus preguntas de una en una, dos minutos cada una, y tu respuesta queda como criterio de la casa con tu nombre.
            Elige las áreas donde tienes criterio propio, por tu sector y tu experiencia.
            <Button variant="primary" onClick={() => abrirAreas()}>Elegir áreas…</Button>
          </Empty>
        )}

        {view === "cargando" && (
          <Table busy label="Cargando la biblioteca">
            <Cabeceras orden={orden} sort={sort} vivo={false} />
            <SkeletonRows />
          </Table>
        )}

        {sinConexion && (
          <>
            <Banner tone="danger" className="banner-gap">
              <b>No llego a la biblioteca.</b> {data ? "Lo que ves puede estar desactualizado y ahora no se puede firmar." : "Y aún no tengo una copia que enseñarte."} Reintento solo cada 30 s — o fuerza uno ahora.{" "}
              <Button variant="link" onClick={() => void b.refetch()}>reintentar ahora</Button>
            </Banner>
            {data && <div inert>{tabla(false, filas.slice(0, 6))}</div>}
          </>
        )}
      </div>
      {data && <ElegirAreas api={api} data={data} open={modal !== null} marcar={modal?.marcar ?? null} onClose={() => setModal(null)} />}
    </>
  );
}
